import React, { useEffect, useMemo, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, toIsoDate, todayIso } from '../utils/dates';
import { t } from '../utils/translations';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Products.css';
import './CustomerReport.css';

const firstOfMonth = () => {
  const d = new Date();
  return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const emptyCreditForm = () => ({
  payment_date: todayIso(),
  method: 'cash',
  notes: '',
  amounts: {}
});

const CustomerReport = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    from: firstOfMonth(),
    to: todayIso(),
    search: '',
    balance: 'all'
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [creditForm, setCreditForm] = useState(emptyCreditForm());
  const [creditSaving, setCreditSaving] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async (nextFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (nextFilters.from) params.set('from', nextFilters.from);
      if (nextFilters.to) params.set('to', nextFilters.to);
      if (nextFilters.search) params.set('search', nextFilters.search);
      if (nextFilters.balance && nextFilters.balance !== 'all') {
        params.set('balance', nextFilters.balance);
      }

      const res = await axios.get(`/api/reports/customers?${params.toString()}`);
      setSummary(res.data.summary || null);
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load customer report');
    } finally {
      setLoading(false);
    }
  };

  const buildOpenInvoicesFromList = (invoices, customerId) => {
    return (invoices || [])
      .filter((inv) => String(inv.customer_id) === String(customerId))
      .filter((inv) => !Number(inv.is_dummy))
      .filter((inv) => String(inv.status || '').toLowerCase() !== 'cancelled')
      .map((inv) => {
        const total = Math.round((parseFloat(inv.total_amount) || 0) * 1000) / 1000;
        const status = String(inv.status || '').toLowerCase();
        let paid = Math.round((parseFloat(inv.paid_amount) || 0) * 1000) / 1000;
        if (paid <= 0 && (status === 'paid' || status === 'completed')) {
          paid = total;
        }
        const remaining = Math.round(Math.max(0, total - paid) * 1000) / 1000;
        return {
          id: inv.id,
          invoice_number: inv.invoice_number,
          issue_date: inv.issue_date,
          total_amount: total,
          paid_amount: paid,
          remaining_amount: remaining,
          status: inv.status
        };
      })
      .filter((inv) => inv.remaining_amount > 0.001)
      .sort((a, b) => String(a.issue_date || '').localeCompare(String(b.issue_date || '')));
  };

  const loadOpenInvoices = async (customerId, statementData) => {
    if (Array.isArray(statementData?.open_invoices) && statementData.open_invoices.length > 0) {
      setOpenInvoices(statementData.open_invoices);
      return statementData.open_invoices;
    }
    try {
      const res = await axios.get('/api/invoices');
      const list = buildOpenInvoicesFromList(res.data.invoices || [], customerId);
      setOpenInvoices(list);
      return list;
    } catch (error) {
      console.error(error);
      setOpenInvoices(statementData?.open_invoices || []);
      return statementData?.open_invoices || [];
    }
  };

  const openStatement = async (customer, options = {}) => {
    const { openCredit = false } = options;
    setSelectedCustomer(customer);
    setStatement(null);
    setOpenInvoices([]);
    setShowCreditForm(false);
    setCreditForm(emptyCreditForm());
    setStatementLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const res = await axios.get(`/api/reports/customers/${customer.id}?${params.toString()}`);
      setStatement(res.data);
      const list = await loadOpenInvoices(customer.id, res.data);
      if (openCredit) {
        const amounts = {};
        (list || []).forEach((inv) => {
          amounts[inv.id] = '';
        });
        setCreditForm({ ...emptyCreditForm(), amounts });
        setShowCreditForm(true);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load customer statement');
      setSelectedCustomer(null);
    } finally {
      setStatementLoading(false);
    }
  };

  const refreshStatement = async () => {
    if (!selectedCustomer) return;
    const params = new URLSearchParams();
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    const res = await axios.get(`/api/reports/customers/${selectedCustomer.id}?${params.toString()}`);
    setStatement(res.data);
    await loadOpenInvoices(selectedCustomer.id, res.data);
    fetchReport(filters);
  };

  const creditAllocations = useMemo(() => {
    return openInvoices
      .map((inv) => {
        const amount = parseFloat(creditForm.amounts[inv.id]);
        return {
          invoice_id: inv.id,
          invoice_number: inv.invoice_number,
          remaining_amount: inv.remaining_amount,
          amount: Number.isFinite(amount) ? amount : 0
        };
      })
      .filter((row) => row.amount > 0);
  }, [openInvoices, creditForm.amounts]);

  const creditTotal = useMemo(
    () => creditAllocations.reduce((sum, row) => sum + row.amount, 0),
    [creditAllocations]
  );

  const startCredit = (invoiceId = '') => {
    const amounts = {};
    openInvoices.forEach((inv) => {
      amounts[inv.id] = invoiceId && inv.id === invoiceId ? String(inv.remaining_amount) : '';
    });
    setCreditForm({
      ...emptyCreditForm(),
      amounts
    });
    setShowCreditForm(true);
  };

  const setCreditAmount = (invoiceId, value) => {
    setCreditForm((prev) => ({
      ...prev,
      amounts: {
        ...prev.amounts,
        [invoiceId]: value
      }
    }));
  };

  const fillAllRemaining = () => {
    const amounts = {};
    openInvoices.forEach((inv) => {
      amounts[inv.id] = String(inv.remaining_amount);
    });
    setCreditForm((prev) => ({ ...prev, amounts }));
  };

  const clearAllAmounts = () => {
    const amounts = {};
    openInvoices.forEach((inv) => {
      amounts[inv.id] = '';
    });
    setCreditForm((prev) => ({ ...prev, amounts }));
  };

  const submitCredit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    if (!creditAllocations.length) {
      alert('Enter a credit amount for at least one invoice');
      return;
    }

    for (const row of creditAllocations) {
      if (row.amount > row.remaining_amount + 0.001) {
        alert(
          `${row.invoice_number}: amount cannot exceed remaining ${formatCurrency(row.remaining_amount)}`
        );
        return;
      }
    }

    try {
      setCreditSaving(true);
      await axios.post('/api/invoices/payments/batch', {
        customer_id: selectedCustomer.id,
        payment_date: creditForm.payment_date || todayIso(),
        method: creditForm.method || null,
        notes: creditForm.notes || null,
        allocations: creditAllocations.map((row) => ({
          invoice_id: row.invoice_id,
          amount: row.amount
        }))
      });
      setShowCreditForm(false);
      setCreditForm(emptyCreditForm());
      await refreshStatement();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to record credit');
    } finally {
      setCreditSaving(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchReport(filters);
  };

  const clearFilters = () => {
    const reset = {
      from: firstOfMonth(),
      to: todayIso(),
      search: '',
      balance: 'all'
    };
    setFilters(reset);
    fetchReport(reset);
  };

  const balanceClass = (status) => {
    if (status === 'due') return 'balance-due';
    if (status === 'credit') return 'balance-credit';
    return 'balance-settled';
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading && !summary) {
    return (
      <div className="page-container">
        <div className="container">{t('Loading...')}</div>
      </div>
    );
  }

  return (
    <div className="page-container customer-report-page">
      <div className="container">
        <div className="page-header no-print">
          <h1>Customer Report</h1>
          <button type="button" className="btn btn-secondary" onClick={handlePrint}>
            Print
          </button>
        </div>

        <div className="card no-print" style={{ marginBottom: '16px' }}>
          <form onSubmit={applyFilters} className="report-filters">
            <div className="form-group">
              <label>From</label>
              <DateInput
                value={filters.from}
                onChange={(e) => handleFilterChange('from', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To</label>
              <DateInput
                value={filters.to}
                onChange={(e) => handleFilterChange('to', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Name, phone, email"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Balance</label>
              <select
                value={filters.balance}
                onChange={(e) => handleFilterChange('balance', e.target.value)}
              >
                <option value="all">All</option>
                <option value="due">Due only</option>
                <option value="settled">Settled</option>
                <option value="credit">Credit balance</option>
              </select>
            </div>
            <div className="report-filter-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading...' : 'Apply'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                Reset
              </button>
            </div>
          </form>
          <p className="report-help">
            Debit = sales (invoices). Credit = amounts credited against invoices (full or partial).
            Balance = Debit − Credit. Open a statement to credit one payment across multiple invoices.
          </p>
        </div>

        {summary && (
          <div className="report-summary-grid">
            <div className="report-summary-card">
              <span>Customers</span>
              <strong>{summary.customer_count}</strong>
            </div>
            <div className="report-summary-card debit">
              <span>Total Debit (Sales)</span>
              <strong>{formatCurrency(summary.total_debit)}</strong>
            </div>
            <div className="report-summary-card credit">
              <span>Total Credit (Paid)</span>
              <strong>{formatCurrency(summary.total_credit)}</strong>
            </div>
            <div className="report-summary-card balance">
              <span>Outstanding Balance</span>
              <strong>{formatCurrency(summary.total_balance)}</strong>
            </div>
            <div className="report-summary-card returns">
              <span>Returns</span>
              <strong>{formatCurrency(summary.total_returns)}</strong>
            </div>
          </div>
        )}

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Invoices</th>
                <th>Debit (Sales)</th>
                <th>Credit (Paid)</th>
                <th>Returns</th>
                <th>Balance</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    No customers found for the selected filters
                  </td>
                </tr>
              ) : (
                customers.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="customer-name-cell">{row.name}</div>
                      {row.email ? <div className="customer-sub">{row.email}</div> : null}
                    </td>
                    <td>{row.phone || '-'}</td>
                    <td>{row.invoice_count}</td>
                    <td>{formatCurrency(row.total_debit)}</td>
                    <td>{formatCurrency(row.total_credit)}</td>
                    <td>{formatCurrency(row.total_returns)}</td>
                    <td>
                      <span className={`balance-pill ${balanceClass(row.balance_status)}`}>
                        {formatCurrency(row.balance)}
                      </span>
                    </td>
                    <td className="no-print">
                      <KebabMenu
                        menuId={`customer-report-${row.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        items={[
                          { label: 'Statement', icon: MENU_ICONS.view, onClick: () => openStatement(row) },
                          row.balance_status === 'due' && {
                            label: 'Credit',
                            icon: MENU_ICONS.settle,
                            onClick: () => openStatement(row, { openCredit: true })
                          }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedCustomer && (
          <div className="modal statement-modal">
            <div className="modal-content statement-modal-content">
              <div className="modal-header no-print">
                <h2>Customer Statement — {selectedCustomer.name}</h2>
                <div className="modal-header-actions">
                  {!statementLoading && openInvoices.length > 0 && !showCreditForm ? (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => startCredit()}>
                      Credit Amount
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="close-btn"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setStatement(null);
                      setOpenInvoices([]);
                      setShowCreditForm(false);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {statementLoading || !statement ? (
                <div style={{ padding: '24px' }}>{t('Loading...')}</div>
              ) : (
                <div className="statement-body">
                  <div className="statement-meta">
                    <div>
                      <strong>{statement.customer.name}</strong>
                      {statement.customer.phone ? <div>{statement.customer.phone}</div> : null}
                      {statement.customer.email ? <div>{statement.customer.email}</div> : null}
                      {statement.customer.address ? <div>{statement.customer.address}</div> : null}
                    </div>
                    <div className="statement-period">
                      Period: {filters.from || '…'} to {filters.to || '…'}
                    </div>
                  </div>

                  <div className="statement-summary">
                    <div>
                      <span>Debit</span>
                      <strong>{formatCurrency(statement.summary.total_debit)}</strong>
                    </div>
                    <div>
                      <span>Credit</span>
                      <strong>{formatCurrency(statement.summary.total_credit)}</strong>
                    </div>
                    <div>
                      <span>Returns</span>
                      <strong>{formatCurrency(statement.summary.total_returns)}</strong>
                    </div>
                    <div>
                      <span>Balance</span>
                      <strong className={balanceClass(
                        statement.summary.balance > 0.001
                          ? 'due'
                          : statement.summary.balance < -0.001
                            ? 'credit'
                            : 'settled'
                      )}>
                        {formatCurrency(statement.summary.balance)}
                      </strong>
                    </div>
                  </div>

                  <div className="credit-panel no-print">
                    <div className="credit-panel-header">
                      <div>
                        <h3>Credit against invoices</h3>
                        <p>
                          Record one payment across multiple invoices. Enter a full or partial amount on each invoice.
                        </p>
                      </div>
                      {!showCreditForm && openInvoices.length > 0 ? (
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => startCredit()}>
                          Credit Amount
                        </button>
                      ) : null}
                    </div>

                    {openInvoices.length === 0 ? (
                      <p className="credit-empty">No open invoices for this customer.</p>
                    ) : !showCreditForm ? (
                      <table className="table open-invoices-table">
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th>Date</th>
                            <th>Total</th>
                            <th>Credited</th>
                            <th>Remaining</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openInvoices.map((inv) => (
                            <tr key={inv.id}>
                              <td>{inv.invoice_number}</td>
                              <td>{formatDate(inv.issue_date)}</td>
                              <td>{formatCurrency(inv.total_amount)}</td>
                              <td>{formatCurrency(inv.paid_amount)}</td>
                              <td>{formatCurrency(inv.remaining_amount)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => startCredit(inv.id)}
                                >
                                  Credit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <form className="credit-form multi-credit-form" onSubmit={submitCredit}>
                        <div className="credit-form-toolbar">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={fillAllRemaining}>
                            Fill all remaining
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={clearAllAmounts}>
                            Clear amounts
                          </button>
                          <strong className="credit-total">
                            Payment total: {formatCurrency(creditTotal)}
                            {creditAllocations.length
                              ? ` · ${creditAllocations.length} invoice${creditAllocations.length === 1 ? '' : 's'}`
                              : ''}
                          </strong>
                        </div>

                        <table className="table open-invoices-table credit-allocate-table">
                          <thead>
                            <tr>
                              <th>Invoice</th>
                              <th>Date</th>
                              <th>Remaining</th>
                              <th>Credit amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {openInvoices.map((inv) => (
                              <tr key={inv.id}>
                                <td>{inv.invoice_number}</td>
                                <td>{formatDate(inv.issue_date)}</td>
                                <td>
                                  {formatCurrency(inv.remaining_amount)}
                                  <button
                                    type="button"
                                    className="linkish"
                                    style={{ display: 'block', marginTop: 4 }}
                                    onClick={() => setCreditAmount(inv.id, String(inv.remaining_amount))}
                                  >
                                    Use remaining
                                  </button>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    placeholder="0"
                                    value={creditForm.amounts[inv.id] ?? ''}
                                    onChange={(e) => setCreditAmount(inv.id, e.target.value)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="credit-meta-grid">
                          <div className="form-group">
                            <label>Date</label>
                            <DateInput
                              value={creditForm.payment_date}
                              onChange={(e) => setCreditForm((prev) => ({ ...prev, payment_date: e.target.value }))}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Method</label>
                            <select
                              value={creditForm.method}
                              onChange={(e) => setCreditForm((prev) => ({ ...prev, method: e.target.value }))}
                            >
                              <option value="cash">Cash</option>
                              <option value="online">Online</option>
                              <option value="upi">UPI</option>
                              <option value="card">Card</option>
                              <option value="bank">Bank transfer</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="form-group credit-notes">
                            <label>Notes</label>
                            <input
                              type="text"
                              placeholder="Optional — applies to this payment"
                              value={creditForm.notes}
                              onChange={(e) => setCreditForm((prev) => ({ ...prev, notes: e.target.value }))}
                            />
                          </div>
                        </div>

                        <div className="credit-form-actions">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={creditSaving || creditAllocations.length === 0}
                          >
                            {creditSaving ? 'Saving...' : 'Save payment'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowCreditForm(false);
                              setCreditForm(emptyCreditForm());
                            }}
                            disabled={creditSaving}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  <table className="table statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Return</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.transactions.length === 0 ? (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center' }}>
                            No transactions in this period
                          </td>
                        </tr>
                      ) : (
                        statement.transactions.map((tx) => (
                          <tr key={tx.id} className={`tx-row tx-${tx.type}`}>
                            <td>{formatDate(tx.date)}</td>
                            <td>
                              <span className={`tx-type tx-type-${tx.type}`}>
                                {tx.type}
                              </span>
                            </td>
                            <td>{tx.ref || '-'}</td>
                            <td>{tx.description}</td>
                            <td>{tx.debit ? formatCurrency(tx.debit) : '-'}</td>
                            <td>{tx.credit ? formatCurrency(tx.credit) : '-'}</td>
                            <td>
                              {tx.return_amount ? formatCurrency(tx.return_amount) : '-'}
                            </td>
                            <td>{formatCurrency(tx.balance)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  <div className="statement-actions no-print">
                    <button type="button" className="btn btn-primary" onClick={handlePrint}>
                      Print Statement
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setStatement(null);
                        setShowCreditForm(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerReport;
